import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';
import { playDing } from '../utils/playDing';
import './NotificationCenter.css';

export default function NotificationCenter() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const prevUnreadRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // Poll every 60s
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const { data } = await API.get('/notifications?limit=10');
      const list = data.notifications || [];
      const count = list.filter(n => !n.is_read).length;
      setNotifications(list);
      setUnreadCount(count);
      if (prevUnreadRef.current !== null && count > prevUnreadRef.current) {
        playDing();
      }
      prevUnreadRef.current = count;
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await API.patch(`/notifications/${notificationId}/read`);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="notification-center">
      <button
        className="notification-bell"
        onClick={() => setShowDropdown(!showDropdown)}
        title="Notifications"
      >
        🔔
        {unreadCount > 0 && <span className="badge">{unreadCount}</span>}
      </button>

      {showDropdown && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notifications</h3>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              {unreadCount > 0 && (
                <button
                  className="mark-all-read"
                  onClick={() => {
                    notifications.forEach(n => !n.is_read && markAsRead(n.id));
                  }}
                >
                  Mark all as read
                </button>
              )}
              <button
                onClick={() => setShowDropdown(false)}
                style={{ background:'none', border:'none', cursor:'pointer', fontSize:18, color:'var(--text-soft)', lineHeight:1, padding:'2px 4px', borderRadius:4 }}
                title="Close"
              >✕</button>
            </div>
          </div>

          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="empty-state">No notifications yet</div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification.id}
                  className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                  onClick={() => {
                    if (!notification.is_read) markAsRead(notification.id);
                    const d = notification.data || {};
                    if (notification.type === 'new_message' && d.conversationId) {
                      navigate(`/messages/${d.conversationId}`);
                    } else if (d.orderId) {
                      navigate(`/orders/${d.orderId}`);
                    } else if (d.listingId) {
                      navigate(`/listing/${d.listingId}`);
                    }
                    setShowDropdown(false);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="notification-content">
                    <div className="notification-title">{notification.title}</div>
                    <div className="notification-message">{notification.body}</div>
                    {notification.data && (
                      <div className="notification-details">
                        {notification.data.pickupDate && (
                          <div>📅 {notification.data.pickupDate}</div>
                        )}
                        {notification.data.location && (
                          <div>📍 {notification.data.location}</div>
                        )}
                      </div>
                    )}
                    <div className="notification-time">{formatDate(notification.created_at)}</div>
                  </div>
                  {!notification.is_read && <div className="unread-indicator" />}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
